import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Phone, Lock, Loader2, Truck } from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DriverLogin = () => {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await safeFetch(`${API_URL}/api/go/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, password })
      });
      if (!result.ok) {
        setError(result.data?.detail || 'Login failed');
        return;
      }
      localStorage.setItem('driver_token', result.data.access_token);
      localStorage.setItem('driver_user', JSON.stringify(result.data.driver));
      navigate('/go/dashboard');
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4" data-testid="driver-login-page">
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#10B981]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#10B981]/5 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md border-0 shadow-2xl bg-[#1E293B] relative z-10">
        <CardContent className="p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#059669] mb-4">
              <Truck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-extrabold text-white tracking-tight">
              TRANSA <span className="text-[#10B981]">GO</span>
            </h1>
            <p className="text-[#94A3B8] text-sm mt-2">Driver Partner Portal</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="text-xs font-medium text-[#94A3B8] mb-1.5 block uppercase tracking-wide">Mobile Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  value={mobile}
                  onChange={e => setMobile(e.target.value)}
                  placeholder="0412 345 678"
                  className="pl-10 h-12 bg-[#0F172A] border-[#334155] text-white placeholder:text-[#4B5563] focus:border-[#10B981] focus:ring-[#10B981]/20"
                  data-testid="driver-login-mobile"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#94A3B8] mb-1.5 block uppercase tracking-wide">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 h-12 bg-[#0F172A] border-[#334155] text-white placeholder:text-[#4B5563] focus:border-[#10B981] focus:ring-[#10B981]/20"
                  data-testid="driver-login-password"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !mobile || !password}
              className="w-full h-12 bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-semibold text-base"
              data-testid="driver-login-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-[#475569] text-xs mt-6">
            Contact your administrator if you need access
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverLogin;
